import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import styles from './styles.module.scss';
import clsx from 'clsx';

interface IProps {
  src: string;
  polygon: any | null;
}

const defaultZoomScale = 2;
const maxZoomScale = 4;

export const TextTractImageView = ({ src, polygon }: IProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef: any = useRef();
  const canvasRef: any = useRef();

  const [zoom, setZoom] = useState<boolean>(false);
  const [zoomScale, setZoomScale] = useState<number>(defaultZoomScale);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);

  const positionCanvas = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current as any;
    if (img && canvas) {
      // Update the canvas position to match that of the image.
      canvas.style.position = 'absolute';
      canvas.style.left = img.offsetLeft + 'px';
      canvas.style.top = img.offsetTop + 'px';
      canvas.style.width = img.width + 'px';
      canvas.style.height = img.height + 'px';
      canvas.style.transform = img.style.transform;

      const context = canvas.getContext('2d');

      img.style.transform = 'unset';
      img.style.transformOrigin = 'unset';
      canvas.style.transform = 'unset';
      canvas.style.transformOrigin = 'unset';
      context.clearRect(0, 0, canvas.width, canvas.height);

      if (polygon) {
        context.strokeStyle = '#00D622';
        context.fillStyle = 'rgba(0, 214, 34, 0.1)';
        context.lineWidth = 1;

        const points = polygon;

        const scale = 2;
        const scaleProportion = 2;
        img.style.transform = `scale(${scale})`;
        img.style.transformOrigin = `${
          scaleProportion * canvas.width * points[0].X
        }px ${scaleProportion * canvas.height * points[0].Y}px `;
        canvas.style.transformOrigin = `${
          scaleProportion * canvas.width * points[0].X
        }px ${scaleProportion * canvas.height * points[0].Y}px`;
        canvas.style.transform = `scale(${scale})`;

        context.beginPath();
        context.moveTo(canvas.width * points[0].X, canvas.height * points[0].Y);
        points
          .slice(1)
          .forEach((point: any) =>
            context.lineTo(canvas.width * point.X, canvas.height * point.Y)
          );
        context.closePath();
        context.fill();
        context.stroke();
      }
    }
  };

  useLayoutEffect(() => {
    window.addEventListener('resize', positionCanvas);
    positionCanvas();
    return () => window.removeEventListener('resize', positionCanvas);
  }, [polygon, src]);

  const handleMouseOver = (): void => {
    setZoom(true);
    setZoomScale(defaultZoomScale);
  };

  const handleMouseOut = (): void => {
    setZoom(false);
    setZoomScale(defaultZoomScale);
  };

  const handleMouseMovement = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!wrapperRef.current) return;

    const { left: offsetLeft, top: offsetTop } =
      wrapperRef.current.getBoundingClientRect();

    const { clientHeight: height, clientWidth: width } = wrapperRef.current;

    const x = ((e.pageX - offsetLeft) / width) * 100;
    const y = ((e.pageY - offsetTop) / height) * 100;

    setMouseX(x);
    setMouseY(y);
  };

  const handleClick = () => {
    setZoomScale((prevState) => {
      const newScale = prevState + 0.5;
      return newScale <= maxZoomScale ? newScale : defaultZoomScale;
    });
  };

  const transform: React.CSSProperties = {
    transformOrigin: mouseX && mouseY ? `${mouseX}% ${mouseY}%` : 'center',
  };

  return (
    <div
      className={clsx(
        styles.root,
        zoomScale === maxZoomScale && styles.zoomOut
      )}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onMouseMove={handleMouseMovement}
      onClick={handleClick}
      ref={wrapperRef}
    >
      {imgRef && (
        <img
          ref={imgRef}
          src={src}
          alt="No image"
          style={{
            ...transform,
            transform: zoom ? `scale(${zoomScale})` : 'scale(1.0)',
          }}
        />
      )}
      <canvas ref={canvasRef} />
    </div>
  );
};
